import React, { useState, useEffect } from 'react';
import { getAllUsers, getPendingUsers, getExpiringUsers, getExpiredUsers, approveUser, revokeUser } from '../services/api';
import NavigationBar from '../components/NavigationBar';

function AdminPage({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, [activeTab]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      let data;
      switch (activeTab) {
        case 'pending':
          data = await getPendingUsers(token);
          break;
        case 'expiring':
          data = await getExpiringUsers(token, 30);
          break;
        case 'expired':
          data = await getExpiredUsers(token);
          break;
        case 'all':
        default:
          data = await getAllUsers(token);
          break;
      }
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => onLogout(), 2000);
      } else if (err.response?.status === 403) {
        setError('Admin access required.');
        setTimeout(() => onLogout(), 2000);
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, username) => {
    try {
      await approveUser(userId, token);
      setMessage(`User ${username} approved successfully`);
      loadUsers(); // Reload the list
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to approve user');
      }
    }
  };

  const handleRevoke = async (userId, username) => {
    if (window.confirm(`Are you sure you want to revoke approval for ${username}?`)) {
      try {
        await revokeUser(userId, token);
        setMessage(`User ${username} approval revoked successfully`);
        loadUsers(); // Reload the list
      } catch (err) {
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to revoke user approval');
        }
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return null;
    
    const expDate = new Date(expirationDate);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiration <= 0) {
      return { text: 'EXPIRED', color: '#dc3545', bg: '#f8d7da' };
    } else if (daysUntilExpiration <= 30) {
      return { text: `${daysUntilExpiration} days`, color: '#856404', bg: '#fff3cd' };
    } else {
      return { text: `${daysUntilExpiration} days`, color: '#155724', bg: '#d4edda' };
    }
  };

  // Styles
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  };



  const tabContainerStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: '20px'
  };

  const tabHeaderStyle = {
    display: 'flex',
    borderBottom: '1px solid #dee2e6',
    padding: '0'
  };

  const tabStyle = (isActive) => ({
    padding: '15px 20px',
    cursor: 'pointer',
    borderBottom: isActive ? '3px solid #3b82f6' : '3px solid transparent',
    backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.8)',
    color: isActive ? '#3b82f6' : '#495057',
    fontWeight: isActive ? 'bold' : 'normal',
    transition: 'all 0.3s ease'
  });

  const contentStyle = {
    padding: '20px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '10px'
  };

  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    fontWeight: 'bold',
    color: '#495057'
  };

  const tdStyle = {
    padding: '12px',
    border: '1px solid #dee2e6',
    verticalAlign: 'top'
  };

  const buttonStyle = {
    padding: '6px 12px',
    margin: '2px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  };

  const approveButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white'
  };

  const revokeButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #dc3545 0%, #c53030 100%)',
    color: 'white'
  };

  const statusBadgeStyle = (approved) => ({
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    backgroundColor: approved ? '#d4edda' : '#fff3cd',
    color: approved ? '#155724' : '#856404',
    border: `1px solid ${approved ? '#c3e6cb' : '#ffeaa7'}`
  });

  const errorStyle = {
    color: '#721c24',
    backgroundColor: '#f8d7da',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #f5c6cb',
    marginBottom: '15px'
  };

  const successStyle = {
    color: '#155724',
    backgroundColor: '#d4edda',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #c3e6cb',
    marginBottom: '15px'
  };



  return (
    <div style={containerStyle}>
      <NavigationBar 
        token={token} 
        onLogout={onLogout}
        title="Admin Dashboard"
        subtitle="User Management & Approval System"
      />

      {/* Tab Navigation */}
      <div style={tabContainerStyle}>
        <div style={tabHeaderStyle}>
          <div 
            style={tabStyle(activeTab === 'pending')}
            onClick={() => setActiveTab('pending')}
          >
            Pending Approval ({users.filter(u => !u.approved && !u.is_admin).length})
          </div>
          <div 
            style={tabStyle(activeTab === 'expiring')}
            onClick={() => setActiveTab('expiring')}
          >
            Expiring Soon
          </div>
          <div 
            style={tabStyle(activeTab === 'expired')}
            onClick={() => setActiveTab('expired')}
          >
            Expired
          </div>
          <div 
            style={tabStyle(activeTab === 'all')}
            onClick={() => setActiveTab('all')}
          >
            All Users
          </div>
        </div>

        <div style={contentStyle}>
          {error && <div style={errorStyle}>{error}</div>}
          {message && <div style={successStyle}>{message}</div>}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No users found in this category.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Badge & Department</th>
                    <th style={thStyle}>Station Contact</th>
                    <th style={thStyle}>Status</th>
                    {(activeTab === 'expiring' || activeTab === 'expired' || activeTab === 'all') && (
                      <th style={thStyle}>Approval Dates</th>
                    )}
                    <th style={thStyle}>Profile</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    if (user.is_admin) return null; // Skip admin users
                    
                    const expirationStatus = getExpirationStatus(user.approval_expires_at);
                    
                    return (
                      <tr key={user.id}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 'bold' }}>{user.username}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {user.email || 'No email'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            ID: {user.id}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Joined: {formatDate(user.created_at)}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 'bold' }}>
                            {user.badge_number || 'No badge'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {user.detachment || 'No department'}
                          </div>
                          {user.station_address && (
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                              📍 {user.station_address.substring(0, 50)}...
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {user.station_phone && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              📞 {user.station_phone}
                            </div>
                          )}
                          {user.station_email && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              ✉️ {user.station_email}
                            </div>
                          )}
                          {!user.station_phone && !user.station_email && (
                            <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                              No station contact info
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={statusBadgeStyle(user.approved)}>
                            {user.approved ? '✅ Approved' : '⏳ Pending'}
                          </div>
                          {expirationStatus && (
                            <div style={{
                              marginTop: '5px',
                              padding: '3px 6px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              backgroundColor: expirationStatus.bg,
                              color: expirationStatus.color
                            }}>
                              {expirationStatus.text}
                            </div>
                          )}
                        </td>
                        {(activeTab === 'expiring' || activeTab === 'expired' || activeTab === 'all') && (
                          <td style={tdStyle}>
                            <div style={{ fontSize: '12px' }}>
                              <strong>Approved:</strong><br />
                              {formatDate(user.approval_date)}
                            </div>
                            <div style={{ fontSize: '12px', marginTop: '5px' }}>
                              <strong>Expires:</strong><br />
                              {formatDate(user.approval_expires_at)}
                            </div>
                          </td>
                        )}
                        <td style={tdStyle}>
                          <div style={{
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            backgroundColor: user.profileComplete ? '#d4edda' : '#fff3cd',
                            color: user.profileComplete ? '#155724' : '#856404',
                            textAlign: 'center'
                          }}>
                            {user.profileComplete ? '✅ Complete' : '⚠️ Incomplete'}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {!user.approved ? (
                            <button
                              onClick={() => handleApprove(user.id, user.username)}
                              style={approveButtonStyle}
                              disabled={loading}
                            >
                              Approve
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRevoke(user.id, user.username)}
                              style={revokeButtonStyle}
                              disabled={loading}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage; 